// scheduleDates.js — the date primitives every Schedule view shares (#146).
//
// Before this, `jobOnDay` was written out three times (WeekView, MonthView,
// CrewWeekView), `todayISO` three times, `addDays` and `dayLabel` twice each,
// and `fmt` twice (ScheduleBoard, MonthView) — plus a fourth `jobOnDay` in
// utils/agenda.js. All byte-identical apart from MonthView's `todayISO`, which
// delegated to its local `fmt` instead of inlining the same string build.
//
// ── Why that mattered enough to fix ─────────────────────────────────────────
//
// These four functions decide WHICH DAY a job appears on. Drift between two
// copies does not throw and does not look wrong — it puts a job on Tuesday in
// one view and Wednesday in another, and whichever a crew happens to open is
// the one they drive to.
//
// `client/src/apps/shared/jobFields.js` is the precedent: it exists because labels,
// tones and board columns were duplicated across Jobs and Schedule and drifted
// into an active/completed colour inversion (v2.9.5). Same failure shape, same
// fix — one definition, imported everywhere.
//
// ── Dates are compared as STRINGS ───────────────────────────────────────────
//
// `scheduled_date` / `scheduled_end_date` are DATE columns (server/database.js
// :697) and arrive as 'YYYY-MM-DD'. Comparing them as strings is exact and
// timezone-free. A Date is constructed ONLY to shift a day or format a label.
//
// ── One deliberate behaviour change: noon, not midnight ─────────────────────
//
// The copies in WeekView and CrewWeekView parsed at `T00:00:00`. This module
// parses at `T12:00:00`, matching what utils/agenda.js already did and what
// WorkerPaystubs.js:32 does for the same reason.
//
// Midnight is the value that breaks: in a zone whose DST transition happens AT
// midnight, local midnight does not exist on that date and the Date lands on
// the adjacent day. Noon has twelve hours of slack in both directions and can
// never do that. It changes nothing for Connecticut — US DST shifts at 2am, so
// midnight is real here — which is exactly why the old code worked and why the
// bug would only ever have surfaced somewhere else, long after anyone
// remembered this decision.

const pad = (n) => String(n).padStart(2, '0');

/** A Date -> 'YYYY-MM-DD', read in the viewer's own timezone. */
export function toISO(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Today as 'YYYY-MM-DD'. `now` is injectable so tests never depend on the clock. */
export function todayISO(now = new Date()) {
  return toISO(now);
}

/**
 * Parse a date-only string at local NOON — for shifting or formatting only.
 * Never use the result for comparison; compare the strings.
 */
export function atNoon(iso) {
  return new Date(`${iso}T12:00:00`);
}

/** 'YYYY-MM-DD' + n days, still as a string. */
export function addDaysISO(iso, n) {
  const d = atNoon(iso);
  d.setDate(d.getDate() + n);
  return toISO(d);
}

/**
 * Does this job occupy `iso`?
 *
 * Multi-day work spans [scheduled_date, scheduled_end_date] inclusive, so it
 * appears under EVERY day it covers. A three-day patio build is on the list on
 * all three mornings, not only the first.
 */
export function jobOnDay(job, iso) {
  if (!job || !job.scheduled_date) return false;
  const end = job.scheduled_end_date || job.scheduled_date;
  return job.scheduled_date <= iso && end >= iso;
}

/** Column header parts for a day: { wd: 'Mon', n: 2 }. */
export function dayLabel(iso) {
  const d = atNoon(iso);
  return { wd: d.toLocaleDateString(undefined, { weekday: 'short' }), n: d.getDate() };
}

/**
 * The Mon–Sun calendar grid covering `anchor`'s month: whole weeks, starting on
 * the Monday on or before the 1st, ending with the week that contains the last
 * day of the month.
 *
 * ── The bug this replaces (#146) ────────────────────────────────────────────
 *
 * MonthView built this inline and stopped with:
 *
 *     if (i >= 27 && d.getMonth() !== month && ((i + 1) % 7 === 0)) break;
 *
 * `(i + 1) % 7 === 0` is the Sunday that ends a row, so the condition asks for
 * the row's LAST day to be outside the month. When a month ends exactly on a
 * Sunday that day is still inside it, the break never fires that week, and a
 * whole extra week of the following month is rendered.
 *
 * February 2027 is the clean case: 28 days, starting Monday, so the month fills
 * exactly four rows — and produced 35 cells instead of 28. The extra row is
 * correctly marked `is-out` and nothing is wrong in it, which is why this
 * survived: the only symptom is the grid changing height between months, and
 * on a phone that is a screenful of empty squares under a month that had none.
 *
 * The fix asks the question directly — has this row reached the end of the
 * month? — rather than inferring it from the row's last day being elsewhere.
 *
 * The old `i >= 27` floor is gone as redundant, not as an oversight: the last
 * day of a month is never earlier than the 28th, so the first row-end that can
 * satisfy `d >= last` is index 27 regardless. The 42-cell ceiling stays; six
 * rows is the most any month can span.
 *
 * @param {Date} anchor any date within the month to render
 * @returns {Date[]} whole weeks, Monday-first, each at local midnight
 */
export function monthGridDays(anchor) {
  const year = anchor.getFullYear();
  const month = anchor.getMonth();

  const first = new Date(year, month, 1);
  // Day 0 of the NEXT month is the last day of this one.
  const last = new Date(year, month + 1, 0);

  // getDay() is Sunday-first; this grid is Monday-first.
  const startDow = (first.getDay() + 6) % 7;
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - startDow);

  const out = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    out.push(d);
    // End of a row, and the month is behind us — stop.
    if ((i + 1) % 7 === 0 && d >= last) break;
  }
  return out;
}
